import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
  Copy as CopyIcon,
  GitBranch,
  Heart,
  Pencil,
  Pin,
  RotateCcw,
  Share2,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import { useI18n } from "../lib/i18n";

export type MessageToolbarAction =
  | "edit"
  | "retry"
  | "branch"
  | "copy"
  | "share"
  | "pin"
  | "feedback-up"
  | "feedback-down"
  | "feedback-heart";

export interface MessageToolbarProps {
  /** Visible state — usually wired to bubble hover / focus. */
  open: boolean;
  /** Which actions to render. Order = render order. */
  actions?: MessageToolbarAction[];
  /** Fired per-action click. */
  onAction?: (action: MessageToolbarAction) => void;
  /**
   * Subset of ``actions`` to render in the "active" visual state — used
   * by toggles (currently only ``pin``) to surface the persisted-on
   * affordance. Each entry paints the icon in accent-orange so the user
   * can tell at a glance whether the message is already pinned.
   */
  activeActions?: ReadonlyArray<MessageToolbarAction>;
  /** Extra wrapper classes. */
  className?: string;
}

const ICONS: Record<
  MessageToolbarAction,
  React.ComponentType<{ size?: number; strokeWidth?: number }>
> = {
  edit: Pencil,
  retry: RotateCcw,
  branch: GitBranch,
  copy: CopyIcon,
  share: Share2,
  pin: Pin,
  "feedback-up": ThumbsUp,
  "feedback-down": ThumbsDown,
  "feedback-heart": Heart,
};

const LABELS: Record<MessageToolbarAction, MessageDescriptor> = {
  edit: msg`Modifica`,
  retry: msg`Riprova`,
  branch: msg`Biforca conversazione`,
  copy: msg`Copia`,
  share: msg`Condividi`,
  pin: msg`Fissa`,
  "feedback-up": msg`Risposta utile`,
  "feedback-down": msg`Risposta da migliorare`,
  "feedback-heart": msg`Risposta eccellente`,
};

const DEFAULT_AGENT_ACTIONS: MessageToolbarAction[] = ["retry", "branch", "copy", "pin"];

/**
 * MessageToolbar — the action row that sits UNDER a `MessageBubble`,
 * beside the timestamp (Claude / ChatGPT pattern).
 *
 * In-flow, not floating: the parent (`Message`) keeps the row mounted so
 * its height is reserved and hovering never shifts the thread. Only the
 * opacity toggles. The buttons stay mounted while hidden on purpose —
 * the previous `AnimatePresence` version unmounted them, so they could
 * never receive keyboard focus and the toolbar was mouse-only.
 *
 * Chrome-free by design (the "pencil" language): no pill, no border, no
 * shadow, no `backdrop-blur`. Emphasis comes from the ink ladder —
 * idle `ink-3` → hover `ink-1`, accent-orange only for an active toggle.
 *
 * Touch fallback (long-press → bottom-sheet) is wired in the feature
 * consumer; this component is only the row.
 */
export const MessageToolbar = React.forwardRef<HTMLDivElement, MessageToolbarProps>(
  ({ open, actions = DEFAULT_AGENT_ACTIONS, onAction, activeActions, className }, ref) => {
    const { i18n } = useI18n();
    const activeSet = React.useMemo(() => new Set(activeActions ?? []), [activeActions]);
    return (
      <div
        ref={ref}
        role="toolbar"
        aria-label={i18n._(msg`Azioni messaggio`)}
        className={cn(
          "inline-flex shrink-0 flex-wrap items-center gap-1 transition-opacity duration-fast",
          open ? "opacity-100" : "pointer-events-none opacity-0",
          className,
        )}
      >
        {actions.map((a) => {
          const Icon = ICONS[a];
          const isActive = activeSet.has(a);
          return (
            <button
              key={a}
              type="button"
              aria-label={i18n._(LABELS[a])}
              aria-pressed={isActive || undefined}
              title={i18n._(LABELS[a])}
              onClick={() => onAction?.(a)}
              className={cn(
                "inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors duration-fast focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ando-orange",
                isActive ? "text-ando-orange" : "text-ink-3 hover:text-ink-1",
              )}
            >
              <Icon size={12} strokeWidth={1.5} />
            </button>
          );
        })}
      </div>
    );
  },
);
MessageToolbar.displayName = "MessageToolbar";
